"use client";

import { KeyboardArrowDown } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem, Typography, CircularProgress, Tooltip } from "@mui/material";
import Image from "next/image";
import { useTranslations } from "next-intl";
import React, { useState, useEffect } from "react";
import { getAllMarkets, addStoreMarket } from "../../services/marketPlaceServices";
import { colors } from "../../theme/colors";
export default function CompactMarketplaceSelector() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMarketplace, setSelectedMarketplace] = useState("");
  const [allMarkets, setAllMarkets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changing, setChanging] = useState(false);
  const g = useTranslations("general");
  const open = Boolean(anchorEl);

  // Fetch markets from API
  const fetchAllMarkets = async () => {
    try {
      setLoading(true);
      const res = await getAllMarkets(true, false, false);
      setAllMarkets(res.data.markets || []);
      setSelectedMarketplace(res.data.storeData.recently_using_market_place);
    } catch (error) {
      console.error("Failed to fetch markets", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAllMarkets();
  }, []);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleMarketplaceChange = async marketId => {
    try {
      setChanging(true);
      await addStoreMarket({
        market_id: marketId,
        assignMarket: true
      });
      setSelectedMarketplace(marketId);
      handleClose();
      window.location.reload();
    } catch (error) {
      console.error("Error selecting marketplace", error);
    } finally {
      setChanging(false);
    }
  };
  const selectedMarket = allMarkets.find(market => market.id === selectedMarketplace);
  if (loading) {
    return <Box sx={{
      display: 'flex',
      alignItems: 'center',
      width: 32,
      height: 32
    }}>
        <CircularProgress size={16} sx={{
        color: colors.text.tertiary
      }} />
      </Box>;
  }
  return <Box data-sentry-element="Box" data-sentry-component="CompactMarketplaceSelector" data-sentry-source-file="CompactMarketplaceSelector.jsx">
      <Tooltip title={g("SelectMarketplace")} placement="bottom" data-sentry-element="Tooltip" data-sentry-source-file="CompactMarketplaceSelector.jsx">
        <Box data-tour="marketplace-selector" onClick={handleClick} sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        border: `1px solid ${colors.border.light}`,
        borderRadius: '6px',
        backgroundColor: 'white',
        cursor: changing ? 'not-allowed' : 'pointer',
        minWidth: 'auto',
        height: 32,
        '&:hover': {
          backgroundColor: changing ? 'white' : colors.background.light,
          borderColor: changing ? colors.border.light : colors.border.medium
        },
        opacity: changing ? 0.6 : 1,
        transition: 'all 0.2s ease'
      }} data-sentry-element="Box" data-sentry-source-file="CompactMarketplaceSelector.jsx">
          {selectedMarket?.country_code ? <Image src={`/assets/svg/countryflag/${selectedMarket.country_code}.svg`} alt={selectedMarket.language_name} height={16} width={16} /> : <Box sx={{
          width: 16,
          height: 16,
          backgroundColor: colors.background.medium,
          borderRadius: '2px'
        }} />}
          <Typography variant="body2" sx={{
          color: colors.text.secondary,
          fontWeight: 500,
          fontSize: '13px',
          whiteSpace: 'nowrap'
        }} data-sentry-element="Typography" data-sentry-source-file="CompactMarketplaceSelector.jsx">
            Marketplace
          </Typography>
          <KeyboardArrowDown sx={{
          fontSize: 14,
          color: colors.text.tertiary,
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }} data-sentry-element="KeyboardArrowDown" data-sentry-source-file="CompactMarketplaceSelector.jsx" />
        </Box>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} PaperProps={{
      sx: {
        mt: 0.5,
        minWidth: 200,
        maxHeight: 300,
        border: `1px solid ${colors.border.light}`,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
      }
    }} transformOrigin={{
      horizontal: 'right',
      vertical: 'top'
    }} anchorOrigin={{
      horizontal: 'right',
      vertical: 'bottom'
    }} data-sentry-element="Menu" data-sentry-source-file="CompactMarketplaceSelector.jsx">
        {allMarkets.map(market => <MenuItem key={market.market_code} onClick={() => handleMarketplaceChange(market.id)} disabled={changing} sx={{
        py: 1,
        px: 2,
        gap: 1.5,
        backgroundColor: selectedMarketplace === market.id ? colors.background.light : 'transparent',
        '&:hover': {
          backgroundColor: colors.primary.light
        },
        '&.Mui-selected': {
          backgroundColor: colors.background.light
        },
        '&.Mui-disabled': {
          opacity: 0.6
        }
      }}>
            <Image src={`/assets/svg/countryflag/${market.country_code}.svg`} alt={market.language_name} height={20} width={20} />
            <Typography variant="body2" sx={{
          color: selectedMarketplace === market.id ? colors.text.primary : colors.text.secondary,
          fontWeight: selectedMarketplace === market.id ? 600 : 400,
          textTransform: 'capitalize',
          flex: 1
        }}>
              {market.market_title}
            </Typography>
            {changing && selectedMarketplace === market.id && <CircularProgress size={14} sx={{
          color: colors.primary.main
        }} />}
          </MenuItem>)}
      </Menu>
    </Box>;
}